import { Stack, Wrap } from '@chakra-ui/react';
import { EditorState } from 'draft-js';
import * as React from 'react';

import {
  AlignCenterIcon,
  AlignLeftIcon,
  AlignRightIcon,
  BoldIcon,
  ItalicIcon,
  ListIcon,
  UnderlineIcon,
} from '@/imports/ui/chakra/feather';
import { NumberedListIcon } from '@/imports/ui/components/TextEditor/components/Toolbar/components/NumberedListIcon';
import { AddLinkButton } from '@/modules/draft-js-link-plugin';

import { BlockTypeButton } from './components/BlockTypeButton';
import { BlockTypeSelect } from './components/BlockTypeSelect';
import { InlineStyleButton } from './components/InlineStyleButton';
import { TextAlignmentButton } from './components/TextAlignmentButton';

export const Toolbar: React.FC<{
  editorState: EditorState;
  setEditorState: (editorState: EditorState) => void;
  isDisabled?: boolean;
  newView?: boolean;
  /**
   * Extra toolbar items rendered after the built-in controls. Consumers
   * receive the live editor state/setter so they can implement custom
   * actions (e.g. inserting template variables) without modifying the
   * base toolbar.
   */
  toolbarExtras?: (ctx: {
    editorState: EditorState;
    setEditorState: (editorState: EditorState) => void;
    isDisabled?: boolean;
  }) => React.ReactNode;
}> = (props) => {
  const { newView = false } = props;
  return (
    <Stack>
      <Wrap shouldWrapChildren>
        <BlockTypeSelect
          editorState={props.editorState}
          setEditorState={props.setEditorState}
          isDisabled={props.isDisabled}
          newView={newView}
        />
        <InlineStyleButton
          editorState={props.editorState}
          setEditorState={props.setEditorState}
          inlineStyle="BOLD"
          icon={<BoldIcon />}
          aria-label="bold text"
          isDisabled={props.isDisabled}
          color={newView? 'primary.500':''}
          colorScheme={newView? "primary": "gray"}
          borderColor={newView? "primary.500" :"inherit"}
        />
        <InlineStyleButton
          editorState={props.editorState}
          setEditorState={props.setEditorState}
          inlineStyle="ITALIC"
          icon={<ItalicIcon />}
          aria-label="italic text"
          isDisabled={props.isDisabled}
          color={newView? 'primary.500':''}
          colorScheme={newView? "primary": "gray"}
          borderColor={newView? "primary.500" :"inherit"}
        />
        <InlineStyleButton
          editorState={props.editorState}
          setEditorState={props.setEditorState}
          inlineStyle="UNDERLINE"
          icon={<UnderlineIcon />}
          aria-label="underline text"
          isDisabled={props.isDisabled}
          color={newView? 'primary.500':''}
          colorScheme={newView? "primary": "gray"}
          borderColor={newView? "primary.500" :"inherit"}
        />
        <BlockTypeButton
          editorState={props.editorState}
          setEditorState={props.setEditorState}
          blockType="ordered-list-item"
          icon={<NumberedListIcon />}
          aria-label="ordered list"
          isDisabled={props.isDisabled}
          color={newView? 'primary.500':''}
          colorScheme={newView? "primary": "gray"}
          borderColor={newView? "primary.500" :"inherit"}
        />
        <BlockTypeButton
          editorState={props.editorState}
          setEditorState={props.setEditorState}
          blockType="unordered-list-item"
          icon={<ListIcon />}
          aria-label="unordered list"
          isDisabled={props.isDisabled}
          color={newView? 'primary.500':''}
          colorScheme={newView? "primary": "gray"}
          borderColor={newView? "primary.500" :"inherit"}
        />
        <TextAlignmentButton
          editorState={props.editorState}
          setEditorState={props.setEditorState}
          textAlign="left"
          icon={<AlignLeftIcon />}
          aria-label="align left"
          isDisabled={props.isDisabled}
          color={newView? 'primary.500':''}
          colorScheme={newView? "primary": "gray"}
          borderColor={newView? "primary.500" :"inherit"}
        />
        <TextAlignmentButton
          editorState={props.editorState}
          setEditorState={props.setEditorState}
          textAlign="center"
          icon={<AlignCenterIcon />}
          aria-label="align center"
          isDisabled={props.isDisabled}
          color={newView? 'primary.500':''}
          colorScheme={newView? "primary": "gray"}
          borderColor={newView? "primary.500" :"inherit"}
        />
        <TextAlignmentButton
          editorState={props.editorState}
          setEditorState={props.setEditorState}
          textAlign="right"
          icon={<AlignRightIcon />}
          aria-label="align right"
          isDisabled={props.isDisabled}
          color={newView? 'primary.500':''}
          colorScheme={newView? "primary": "gray"}
          borderColor={newView? "primary.500" :"inherit"}
        />
        <AddLinkButton
          editorState={props.editorState}
          setEditorState={props.setEditorState}
          aria-label="link"
          variant="outline"
          colorScheme={newView? "primary": "gray"}
          borderColor={newView? "primary.500" :"inherit"}
          size="sm"
          submitButtonColorScheme="purple"
          isDisabled={props.isDisabled}
          color={newView? 'primary.500':''}
        />
        {props.toolbarExtras?.({
          editorState: props.editorState,
          setEditorState: props.setEditorState,
          isDisabled: props.isDisabled,
        })}
      </Wrap>
    </Stack>
  );
};
